/**
 * StoryView — main story creation screen.
 *
 * Layout (top→bottom):
 *   Header: Story Builder title  |  Style dropdown · Chat · Kebab · Gallery
 *   Middle row: Characters cluster | Muse (VoiceOrb) | Places cluster (CardStage)
 *   Beats: circular-arc carousel of beat cards (BeatsStrip)
 *   Generate/progress button: anchored at the top of the beats band
 */

import React, { useState, useEffect } from "react";
import { View, Text, Pressable, Image, useWindowDimensions } from "react-native";
import StorybuilderLogo from "../../assets/icons/storybuilder-logo.svg";
import { styles } from "./StoryView.styles";
import { colors, useScale, design, getOrbSize } from "../../styles";
import { useSocketStore } from "../../store/socketStore";
import { trackButton, Button, View as MetricView } from "../../services/metrics";
import { useStoryStore } from "../../store/storyStore";
import { useComicStore } from "../../store/comicStore";
import { useFTUEStore } from "../../store/ftueStore";
import { useConfigStore } from "../../store/configStore";


import BeatsStrip from "./BeatsStrip";
import CardStage from "./CardStage";
import VoiceOrb from "../voice/VoiceOrb";
import GalleryIcon from "../icons/GalleryIcon";
import MoreVerticalIcon from "../icons/MoreVerticalIcon";
import { useDebugStore } from "../../store/debugStore";

const DevDebugPanel = (__DEV__ || (typeof window !== "undefined" && new URLSearchParams(window.location?.search || "").get("dev") === "1"))
  ? require("../dev/DevDebugPanel").default
  : null;

import storyBg from "../../assets/story-bg.png";

import StyleSelector from "./StyleSelector";
import LayoutPickerModal from "./LayoutPickerModal";
import GenerateProgressButton from "./GenerateProgressButton";
import OfflineToast from "../OfflineToast";


/** How far the generate button sits below the beats-band top edge (design px). */
const BEATS_BAND_BUTTON_INSET = 10;

// Stable identities so CardStage's layout memo isn't busted each render.
const EMPTY_ENTITIES: never[] = [];
const EMPTY_BEATS: never[] = [];

/* ─── Entity type for the card clusters (see CardStage) ─── */

type StageEntity = {
  type: "character" | "scene";
  entityId: string;
  name: string;
  visualDescription: string;
  imageBase64?: string;
  isLoading?: boolean;
  hasError?: boolean;
  isModerated?: boolean;
};

/* ─── StoryView ─── */

interface StoryViewProps {
  onShowBook: () => void;
  onShowGallery: () => void;
  onClear: () => void;
  onClose: () => void;
}

export default function StoryView({ onShowBook, onShowGallery, onClear, onClose }: StoryViewProps) {
  const { emitEvent, generateComic, isConnected, saveToESS, storyLoading } = useSocketStore();
  const accessBlocked = useSocketStore((st) => st.accessBlocked);
  const { characters, scenes, beats, focusedCharacterId, focusedSceneId } = useStoryStore();
  const genState = useComicStore((st) => st.genState);
  const isBookGenerating = genState === "generating_prompts" || genState === "generating_pages";
  const moderationAvailable = useConfigStore((st) => st.moderationAvailable);
  const [showKebab, setShowKebab] = useState(false);
  const [saving, setSaving] = useState(false);
  const [forceBlockText, setForceBlockText] = useState(false);
  const [forceBlockImage, setForceBlockImage] = useState(false);
  const [forceBlockPlotter, setForceBlockPlotter] = useState(false);
  const [moderationEnabled, setModerationEnabled] = useState(moderationAvailable);
  const debugPanelOpen = useDebugStore((st) => st.panelOpen);
  useEffect(() => { setModerationEnabled(moderationAvailable); }, [moderationAvailable]);
  const { s, scale } = useScale();
  const { width: winW, height: winH } = useWindowDimensions();

  const scaled = {
    headerBtn: { width: s(design.headerBtn.width), height: s(design.headerBtn.height), borderRadius: s(design.headerBtn.radius) },
    headerBtnNarrow: { width: s(design.headerBtnNarrow.width), height: s(design.headerBtnNarrow.height), borderRadius: s(design.headerBtnNarrow.radius) },
    notch: { height: s(80), borderRadius: s(32), paddingHorizontal: s(20), gap: s(8) },
    galleryFontSize: { fontSize: s(18) },
    // Flat fraction of the window height (not width-scaled) — see design.beatsSection.
    beatsSection: { height: Math.round(winH * design.beatsSection.heightFrac) },
  };

  // The beats band occupies the bottom `beatsSection.height`; its top edge is the
  // baseline for the generate button, nudged slightly into the band (whose right
  // side is empty since beats are centered) so it clears the bottom row of cards.
  const beatsBandTop = winH - scaled.beatsSection.height;
  const generateButtonTop = beatsBandTop + s(BEATS_BAND_BUTTON_INSET);

  const handleSaveToCloud = async () => {
    setSaving(true);
    saveToESS();
    setTimeout(() => setSaving(false), 1500);
  };

  const [showLayoutPicker, setShowLayoutPicker] = useState(false);

  const handleBuildBook = () => {
    if (isConnected) {
      setShowLayoutPicker(true);
    }
  };

  const handleGenerateFromPicker = () => {
    setShowLayoutPicker(false);
    trackButton(MetricView.StoryBuilder, Button.StoryBuilder.Generate);
    generateComic();
  };

  const hasContent = !storyLoading && (characters.length > 0 || scenes.length > 0 || beats.length > 0);

  /* Build entity arrays for the card clusters (CardStage). Memoized on the
     source arrays so CardStage's layout memo isn't defeated by a fresh array
     identity on every unrelated render. */
  const charEntities: StageEntity[] = React.useMemo(() => characters.map((c) => ({
    type: "character" as const,
    entityId: c.characterId,
    name: c.name,
    visualDescription: c.visualDescription,
    imageBase64: c.imageBase64,
    isLoading: c.isLoading,
    hasError: c.hasError,
    isModerated: c.isModerated,
  })), [characters]);
  const sceneEntities: StageEntity[] = React.useMemo(() => scenes.map((s) => ({
    type: "scene" as const,
    entityId: s.sceneId,
    name: s.name || "Unnamed",
    visualDescription: s.visualDescription,
    imageBase64: s.imageBase64,
    isLoading: s.isLoading,
    hasError: s.hasError,
    isModerated: s.isModerated,
  })), [scenes]);

  // While loading, the store still holds the previous project — withhold it so
  // the stage shows empty rather than flashing stale content.
  const displayCharEntities = storyLoading ? EMPTY_ENTITIES : charEntities;
  const displaySceneEntities = storyLoading ? EMPTY_ENTITIES : sceneEntities;
  const displayBeats = storyLoading ? EMPTY_BEATS : beats;

  const orbSize = getOrbSize(scale);
  const iconSize = s(design.iconSize);

  const bgSize = Math.max(winW, winH) * 1.1;

  return (
    <View style={styles.container}>
      <Image source={storyBg} style={{ position: "absolute", width: bgSize, height: bgSize, alignSelf: "center", top: -(bgSize - winH) / 2 }} resizeMode="contain" />
      <OfflineToast variant="dark" />
      {/* ── Header ── */}
      <View style={styles.header}>
        <View style={styles.logoArea}>
          <StorybuilderLogo width={s(204)} height={s(52)} accessibilityLabel="Storybuilder" />
          <View style={{ width: 8, height: 8, borderRadius: 4, marginLeft: 8, backgroundColor: isConnected ? colors.success : colors.danger }} />
          {__DEV__ && (
            <View style={{ flexDirection: "row", gap: 4, marginLeft: 8 }}>
              <Pressable
                style={{ paddingHorizontal: 6, paddingVertical: 3, borderRadius: 6, backgroundColor: !moderationAvailable ? "#EF4444" : moderationEnabled ? "#34D399" : "#F59E0B", opacity: moderationAvailable ? 1 : 0.7 }}
                onPress={() => {
                  if (!moderationAvailable) return;
                  const next = !moderationEnabled;
                  setModerationEnabled(next);
                  emitEvent("debugToggleModeration", { enabled: next });
                  if (!next) {
                    setForceBlockText(false);
                    setForceBlockImage(false);
                    emitEvent("debugForceBlock", { target: "text", enabled: false });
                    emitEvent("debugForceBlock", { target: "image", enabled: false });
                  }
                }}
              >
                <Text style={{ fontSize: 10, fontWeight: "700", color: "#FFF" }}>{!moderationAvailable ? "🚫 MOD" : moderationEnabled ? "🛡 MOD" : "⚠️ MOD"}</Text>
              </Pressable>
              <Pressable
                style={{ paddingHorizontal: 6, paddingVertical: 3, borderRadius: 6, backgroundColor: forceBlockText ? "#EF4444" : "#D1D5DB" }}
                onPress={() => {
                  const next = !forceBlockText;
                  setForceBlockText(next);
                  emitEvent("debugForceBlock", { target: "text", enabled: next });
                }}
              >
                <Text style={{ fontSize: 10, fontWeight: "700", color: forceBlockText ? "#FFF" : "#374151" }}>🚫 TXT</Text>
              </Pressable>
              <Pressable
                style={{ paddingHorizontal: 6, paddingVertical: 3, borderRadius: 6, backgroundColor: forceBlockImage ? "#EF4444" : "#D1D5DB" }}
                onPress={() => {
                  const next = !forceBlockImage;
                  setForceBlockImage(next);
                  emitEvent("debugForceBlock", { target: "image", enabled: next });
                }}
              >
                <Text style={{ fontSize: 10, fontWeight: "700", color: forceBlockImage ? "#FFF" : "#374151" }}>🚫 IMG</Text>
              </Pressable>
              <Pressable
                style={{ paddingHorizontal: 6, paddingVertical: 3, borderRadius: 6, backgroundColor: forceBlockPlotter ? "#EF4444" : "#D1D5DB" }}
                onPress={() => {
                  const next = !forceBlockPlotter;
                  setForceBlockPlotter(next);
                  emitEvent("debugForceBlock", { target: "plotter", enabled: next });
                }}
              >
                <Text style={{ fontSize: 10, fontWeight: "700", color: forceBlockPlotter ? "#FFF" : "#374151" }}>🚫 PLOT</Text>
              </Pressable>
            </View>
          )}
          {DevDebugPanel && (
            <Pressable
              style={{ paddingHorizontal: 6, paddingVertical: 3, borderRadius: 6, marginLeft: 8, backgroundColor: debugPanelOpen ? "#8B5CF6" : "#D1D5DB" }}
              onPress={() => useDebugStore.getState().togglePanel()}
            >
              <Text style={{ fontSize: 10, fontWeight: "700", color: debugPanelOpen ? "#FFF" : "#374151" }}>Dev</Text>
            </Pressable>
          )}
        </View>

        <View style={styles.headerRight}>
          {__DEV__ && (
            <View>
              <Pressable style={[styles.iconBtnNarrow, scaled.headerBtnNarrow]} onPress={() => { setShowKebab(!showKebab); }} accessibilityLabel="Menu" accessibilityRole="button">
                <MoreVerticalIcon size={iconSize} />
              </Pressable>
              {showKebab && (
                <View style={styles.kebabDropdown}>
                  <Pressable style={styles.kebabItem} onPress={() => { setShowKebab(false); handleSaveToCloud(); }}>
                    <Text style={styles.kebabItemText}>Save to cloud</Text>
                  </Pressable>
                  <Pressable style={styles.kebabItem} onPress={() => { setShowKebab(false); onClear(); }}>
                    <Text style={styles.kebabItemText}>Start over</Text>
                  </Pressable>
                  <Pressable style={styles.kebabItem} onPress={() => { setShowKebab(false); useFTUEStore.getState().reset(); }}>
                    <Text style={styles.kebabItemText}>Show FTUE</Text>
                  </Pressable>
                </View>
              )}
            </View>
          )}

          {/* Style selector */}
          <StyleSelector onOpen={() => setShowKebab(false)} disabled={isBookGenerating} />
        </View>

        {/* Gallery button */}
        {!accessBlocked && (
          <Pressable style={[styles.galleryBtn, scaled.notch]} onPress={onShowGallery} testID="gallery-btn" accessibilityLabel="My Creations" accessibilityRole="button">
            <GalleryIcon size={s(design.galleryIconSize)} />
            <Text style={[styles.galleryText, scaled.galleryFontSize]}>My Creations</Text>
          </Pressable>
        )}

        <Pressable style={[styles.iconBtn, scaled.headerBtn, { marginRight: s(16) }]} onPress={onClose} accessibilityLabel="Close" accessibilityRole="button">
          <Image source={require("../../assets/icons/close.png")} style={{ width: s(32), height: s(32), tintColor: colors.white }} resizeMode="contain" />
        </Pressable>
      </View>

      {/* Backdrop to dismiss kebab */}
      {showKebab && <Pressable style={styles.backdrop} onPress={() => setShowKebab(false)} />}

      {/* ── Main area — always rendered; a disconnect/load never hides Muse. ── */}
      <>
        {/* Characters (left) and Places (right) clusters around the centered Muse. */}
        <View style={styles.mainRow}>
          <CardStage
            characters={displayCharEntities}
            scenes={displaySceneEntities}
            focusedCharacterId={focusedCharacterId}
            focusedSceneId={focusedSceneId}
            orbWidth={orbSize}
            orb={<VoiceOrb size={orbSize} />}
          />
        </View>

        {/* Beats */}
        <View style={[styles.beatsSection, scaled.beatsSection]}>
          {displayBeats.length > 0 && (
            <BeatsStrip beats={displayBeats} height={scaled.beatsSection.height} />
          )}
        </View>
      </>

      {/* Progress / Generate button — right edge, anchored at the top of the
          beats band (see generateButtonTop) so it clears the Places cards. */}
      {hasContent && (
        <GenerateProgressButton progressStep={Math.min(beats.length, 3)} onPress={handleBuildBook} onViewBook={onShowBook} scale={s} top={generateButtonTop} disabled={!isConnected} />
      )}

      {/* Saving indicator */}
      {saving && (
        <View style={styles.savingOverlay}>
          <Text style={styles.savingText}>Saving...</Text>
        </View>
      )}

      {/* Style panel — removed, now handled by StyleSelector */}

      <LayoutPickerModal
        visible={showLayoutPicker}
        onClose={() => setShowLayoutPicker(false)}
        onGenerate={handleGenerateFromPicker}
      />

      {DevDebugPanel && <DevDebugPanel />}
    </View>
  );
}
